import {
  Growth,
  MetricCard,
  Customer,
  Revenue,
  CustomerClosed,
} from "components/dashboard";
import { MainPageLoader } from "components/loaders";
import { useEffect, useState } from "react";
import {
  getClosedLandlordsWithFilter,
  getCustomersWithFilter,
  getDashboadKpis,
  getGrowthDataWithFilter,
  getRevenueDataWithFilter,
} from "services/dashboard.service";
import { suppressApexChartsErrors } from "utils/errorSuppressionUtils";

function Dashboard() {
  const [widgetData, setWidgetData] = useState({
    customers: { categories: [], data: [], loading: true, error: null },
    customerClosed: { categories: [], data: [], loading: true, error: null },
    revenue: { categories: [], data: [], loading: true, error: null },
    growth: { categories: [], data: [], loading: true, error: null },
    newSignUps: { data: [], loading: true, error: null },
    subscriptions: { data: [], loading: true, error: null },
    revenueMetrics: { data: [], loading: true, error: null },
  });

  const [dateRange, setDateRange] = useState({
    category: "",
    startDate: new Date(),
    endDate: new Date(),
  });

  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const restoreConsoleError = suppressApexChartsErrors();

    return () => {
      restoreConsoleError();
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getDashboadKpis();
        const data = response?.data?.data || {};

        setWidgetData((prev) => ({
          ...prev,
          customers: {
            categories: data.Customers?.categories || [],
            data: data.Customers?.data || [],
            loading: false,
            error: null,
          },
          customerClosed: {
            categories: data.ClosedLandlords?.categories || [],
            data: data.ClosedLandlords?.data || [],
            loading: false,
            error: null,
          },
          growth: {
            categories: data.Growth?.categories || [],
            data: data.Growth?.data || [],
            loading: false,
            error: null,
          },
          revenue: {
            categories: data.Revenue?.categories || [],
            data: data.Revenue?.data || [],
            loading: false,
            error: null,
          },
          newSignUps: {
            data: data.NewSignups || [],
            loading: false,
            error: null,
          },
          subscriptions: {
            data: data.Subscriptions || [],
            loading: false,
            error: null,
          },
          revenueMetrics: {
            data: data.RevenueMetrics || [],
            loading: false,
            error: null,
          },
        }));

        setLoading(false);

        setTimeout(() => {
          setFadeIn(true);
        }, 50);
      } catch (error) {
        setWidgetData((prev) => ({
          ...prev,
          customers: { ...prev.customers, loading: false, error },
          customerClosed: { ...prev.customerClosed, loading: false, error },
          growth: { ...prev.growth, loading: false, error },
          revenue: { ...prev.revenue, loading: false, error },
          newSignUps: { ...prev.newSignUps, loading: false, error },
          subscriptions: { ...prev.subscriptions, loading: false, error },
          revenueMetrics: { ...prev.revenueMetrics, loading: false, error },
        }));
        console.error("Error fetching dashboard KPIs:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCustomersWithDateFilter = async (startDate, endDate) => {
    try {
      setWidgetData((prev) => ({
        ...prev,
        customers: {
          ...prev.customers,
          loading: true,
          error: null,
        },
      }));

      const res = await getCustomersWithFilter(startDate, endDate);
      const responseData = res?.data?.data || {};

      let categories, data;

      if (responseData.Customers) {
        categories = responseData.Customers.categories || [];
        data = responseData.Customers.data || [];
      } else {
        categories = responseData.categories || [];
        data = responseData.data || [];
      }

      setWidgetData((prev) => ({
        ...prev,
        customers: {
          categories,
          data,
          loading: false,
          error: null,
          updateTimestamp: Date.now(),
        },
      }));
    } catch (error) {
      console.error("Error fetching filtered customer data:", error);
      setWidgetData((prev) => ({
        ...prev,
        customers: {
          ...prev.customers,
          loading: false,
          error,
        },
      }));
    }
  };

  const getCustomerClosedWithDateFilter = async (startDate, endDate) => {
    try {
      setWidgetData((prev) => ({
        ...prev,
        customerClosed: {
          ...prev.customerClosed,
          loading: true,
          error: null,
        },
      }));

      const res = await getClosedLandlordsWithFilter(startDate, endDate);
      const responseData = res?.data?.data || {};

      let categories, data;

      if (responseData.ClosedLandlords) {
        categories = responseData.ClosedLandlords.categories || [];
        data = responseData.ClosedLandlords.data || [];
      } else {
        categories = responseData.categories || [];
        data = responseData.data || [];
      }

      setWidgetData((prev) => ({
        ...prev,
        customerClosed: {
          categories,
          data,
          loading: false,
          error: null,
          updateTimestamp: Date.now(),
        },
      }));
    } catch (error) {
      console.error("Error fetching filtered customer closed data:", error);
      setWidgetData((prev) => ({
        ...prev,
        customerClosed: {
          ...prev.customerClosed,
          loading: false,
          error,
        },
      }));
    }
  };

  const getGrowthWithDateFilter = async (startDate, endDate) => {
    try {
      setWidgetData((prev) => ({
        ...prev,
        growth: {
          ...prev.growth,
          loading: true,
          error: null,
        },
      }));

      const res = await getGrowthDataWithFilter(startDate, endDate);
      const responseData = res?.data?.data || {};

      console.log("Growth filter API response:", responseData);

      const categories = responseData.categories || [];
      const data = responseData.data || [];

      setWidgetData((prev) => ({
        ...prev,
        growth: {
          categories,
          data,
          loading: false,
          error: null,
          updateTimestamp: Date.now(),
        },
      }));
    } catch (error) {
      console.error("Error fetching filtered growth data:", error);
      setWidgetData((prev) => ({
        ...prev,
        growth: {
          ...prev.growth,
          loading: false,
          error,
        },
      }));
    }
  };

  const getRevenueWithDateFilter = async (startDate, endDate) => {
    try {
      // Only update loading state for the Revenue widget
      setWidgetData((prev) => ({
        ...prev,
        revenue: {
          ...prev.revenue,
          loading: true,
          error: null,
        },
      }));

      // Fetch data
      const res = await getRevenueDataWithFilter(startDate, endDate);
      const responseData = res?.data?.data || {};

      const categories = responseData.categories || [];
      const data = responseData.data || [];

      setWidgetData((prev) => ({
        ...prev,
        revenue: {
          categories,
          data,
          loading: false,
          error: null,
          updateTimestamp: Date.now(),
        },
      }));
    } catch (error) {
      console.error("Error fetching filtered revenue data:", error);
      setWidgetData((prev) => ({
        ...prev,
        revenue: {
          ...prev.revenue,
          loading: false,
          error,
        },
      }));
    }
  };

  useEffect(() => {
    if (dateRange?.category) {
      switch (dateRange?.category) {
        case "Customer":
          getCustomersWithDateFilter(dateRange?.startDate, dateRange?.endDate);
          break;
        case "New sign ups":
          getCustomerClosedWithDateFilter(
            dateRange?.startDate,
            dateRange?.endDate
          );
          break;
        case "Total Revenue":
          getRevenueWithDateFilter(dateRange?.startDate, dateRange?.endDate);
          break;
        case "Growth":
          getGrowthWithDateFilter(dateRange?.startDate, dateRange?.endDate);
          break;
        default:
          console.log("Default call - no matching category");
          break;
      }
    }
  }, [dateRange]);

  return (
    <div className="p-3 md:p-5 bg-[#FBFAFA] dark:bg-[#1f1f1f] space-y-5 flex flex-col 2xl:items-center min-h-screen">
      {loading ? (
        <div
          className="flex items-center justify-center w-full h-full"
          style={{ minHeight: "calc(100vh - 80px)" }}
        >
          <MainPageLoader />
        </div>
      ) : (
        <div
          className={`transition-opacity duration-500 ease-in-out ${
            fadeIn ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="2xl:w-[1260px]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                title="Revenues"
                value={`$${widgetData.revenueMetrics.data.total}`}
                percentageChange={
                  widgetData.revenueMetrics.data.percentageChange
                }
                reportLink={{
                  label: "Revenues report",
                  href: "/reporting?tab=2",
                }}
              />

              <MetricCard
                title="New sign ups"
                value={widgetData.newSignUps.data.total}
                percentageChange={widgetData.newSignUps.data.percentageChange}
                reportLink={{
                  label: "New signup report",
                  href: "/reporting?tab=1",
                }}
              />

              <MetricCard
                title="Paid Subscriptions"
                value={widgetData.subscriptions.data.total}
                percentageChange={
                  widgetData.subscriptions.data.percentageChange
                }
                reportLink={{
                  label: "Subscriptions report",
                  href: "/reporting?tab=1",
                }}
              />
            </div>
          </div>

          <div className="2xl:w-[1260px] mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 auto-rows-[minmax(532px,auto)] gap-4">
              <div className="transition-all duration-300 ease-in-out">
                <Revenue
                  revenueData={widgetData.revenue}
                  setDateRange={setDateRange}
                  key={`revenue-${
                    widgetData.revenue.updateTimestamp || "initial"
                  }`}
                />
              </div>
              <div className="transition-all duration-300 ease-in-out">
                <Growth
                  growthData={widgetData.growth}
                  setDateRange={setDateRange}
                  key={`growth-${
                    widgetData.growth.updateTimestamp || "initial"
                  }`}
                />
              </div>
              <div className="transition-all duration-300 ease-in-out">
                <Customer
                  customersData={widgetData.customers}
                  setDateRange={setDateRange}
                  key={`customer-${
                    widgetData.customers.updateTimestamp || "initial"
                  }`}
                />
              </div>
              <div className="transition-all duration-300 ease-in-out">
                <CustomerClosed
                  landlordsData={widgetData.customerClosed}
                  setDateRange={setDateRange}
                  key={`customerClosed-${
                    widgetData.customerClosed.updateTimestamp || "initial"
                  }`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default Dashboard;
